import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Copy, CheckCircle2, X, User } from "lucide-react";
import { Text } from "@core/index";
import astroMadlen from "@packages/assets/astromadlen_mirror.png";
import AstroMadlen_Profile_Picture from "@packages/assets/AstroMadlen_Profile_Picture.png";
import {
  useState,
  useRef,
  useEffect,
  useMemo,
  TextareaHTMLAttributes,
  ChangeEvent,
} from "react";
import {
  getGetTeacherToolChatQueryKey,
  useCreateTeacherToolChat,
  useGetTeacherToolChat,
  useSendMessageToTeacherToolChat,
} from "@packages/api/madlenAPI";
import {
  CreateTeacherToolChatRequestDto,
  TeacherToolChatResponseDto,
  TeacherToolChatSendMessageRequestDto,
} from "@packages/api/madlenAPI.schemas";
import { useQueryClient } from "@tanstack/react-query";
import remarkGfm from "remark-gfm";
import Markdown from "react-markdown";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { tr, enUS } from "date-fns/locale";
// Expandable TextArea component
interface AutoResizeTextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  minRows?: number;
  maxRows?: number;
}

const AutoResizeTextarea = ({
  minRows = 1,
  maxRows = 5,
  onChange,
  ...props
}: AutoResizeTextareaProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";

    // Calculate new height
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 24;
    const minHeight = minRows * lineHeight;
    const maxHeight = maxRows * lineHeight;

    // Set new height with limits
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

    textarea.style.height = `${newHeight}px`;

    // Call the original onChange handler
    if (onChange) onChange(e);
  };

  useEffect(() => {
    // Initialize height
    if (textareaRef.current) {
      handleChange({
        target: textareaRef.current,
      } as ChangeEvent<HTMLTextAreaElement>);
    }
  }, [props.value]);

  return (
    <textarea
      ref={textareaRef}
      onChange={handleChange}
      rows={minRows}
      {...props}
      className={`w-full pr-14 pl-5 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent placeholder-gray-500 dark:placeholder-gray-400 resize-none overflow-hidden ${props.className || ""}`}
    />
  );
};

export function MascotBubble({
  createChatParams,
  toolsQueryKey,
  toolQueryKey,
}: {
  createChatParams: CreateTeacherToolChatRequestDto;
  toolsQueryKey: any;
  toolQueryKey: any;
}) {
  return null;
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const [isChatInitialized, setIsChatInitialized] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);

  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  // Animation variants for floating mascot
  const floatingAnimation = {
    y: [0, -8, 0],
    x: [0, 3, 0],
    rotate: [0, 1, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  // Animation variants for sparkles
  const sparkleVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: { scale: 1, opacity: [0, 1, 0] },
  };

  // Animation variants for thinking indicator
  const thinkingVariants = {
    animate: {
      scale: [1, 1.05, 1],
      opacity: [0.7, 1, 0.7],
      transition: {
        duration: 1.2,
        repeat: Infinity,
        ease: "easeInOut",
      },
    },
  };

  // Chat container animation variants
  const chatContainerVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      y: 20,
      transition: {
        duration: 0.2,
      },
    },
  };

  // Message animation variants
  const messageVariants = {
    hidden: { opacity: 0 },
    visible: (i: number) => ({
      opacity: 1,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
        ease: "easeOut",
      },
    }),
  };

  const { data: chat } = useGetTeacherToolChat(Number(chatId), {
    query: {
      refetchInterval: (data) => {
        if (
          (data?.state.data?.messages?.length &&
            data?.state.data?.messages[data?.state.data?.messages.length - 1]
              .role === "USER") ||
          (data?.state.data?.messages?.length &&
            data?.state.data?.messages[data?.state.data?.messages.length - 1]
              .responseType === "REGEN_TOOL")
        ) {
          return 2000;
        }

        return false;
      },
      enabled: !!chatId,
    },
  });

  const { mutate: createChat, isPending: isCreatingChat } =
    useCreateTeacherToolChat({
      mutation: {
        onMutate: () => {
          // Variables is needed for type signature but not used
        },
        onSuccess: (data) => {
          if (data?.id) {
            setChatId(data?.id.toString());
            setIsChatInitialized(true);
            setTimeout(() => {
              setIsExpanded(true);
            }, 700);
          }
        },
      },
    });

  const { mutate: sendMessage } = useSendMessageToTeacherToolChat({
    mutation: {
      onMutate: (variables) => {
        const previousData = queryClient.getQueryData(
          getGetTeacherToolChatQueryKey(Number(chatId)),
        );
        queryClient.setQueryData(
          getGetTeacherToolChatQueryKey(Number(chatId)),
          (oldData: TeacherToolChatResponseDto) => {
            return {
              ...oldData,
              messages: [
                ...(oldData.messages || []),
                {
                  id: `temp-${Date.now()}`,
                  createdAt: new Date().toISOString(),
                  content: variables.data.content,
                  role: "USER",
                },
              ],
            };
          },
        );
        return { previousData };
      },
      onError: (error, _variables, context) => {
        queryClient.setQueryData(
          getGetTeacherToolChatQueryKey(Number(chatId)),
          context?.previousData,
        );
        console.error("Error sending message:", error);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: getGetTeacherToolChatQueryKey(Number(chatId)),
        });
      },
    },
  });

  // Refined scroll effect
  useEffect(() => {
    // Only scroll if the chat is expanded and there are messages
    if (isExpanded && chatContainerRef.current && chat?.messages?.length) {
      // Use setTimeout to ensure scroll happens after DOM updates are flushed
      const timerId = setTimeout(() => {
        if (chatContainerRef.current) {
          // Smooth scroll to the bottom reference div
          chatContainerRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }
      }, 50); // Increased delay slightly to 50ms

      // Cleanup timeout if component unmounts or dependencies change
      return () => clearTimeout(timerId);
    }
    // Depend on message count and expansion state
  }, [chat?.messages?.length, isExpanded]);

  // Reset copied message state after 2 seconds
  useEffect(() => {
    if (copiedMessageId !== null) {
      const timer = setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedMessageId]);

  // Function to handle chat initialization and expansion
  const handleMascotClick = async () => {
    if (isCreatingChat) return;

    const newExpandedState = !isExpanded;

    if (newExpandedState && !isChatInitialized) {
      try {
        await createChat({ data: createChatParams });
      } catch (error) {
        console.error("Error creating chat:", error);
      }
    } else {
      setIsExpanded(newExpandedState);
    }
  };

  // Handle sending a message
  function handleSendMessage() {
    if (!inputValue.trim() || !chatId) return;

    // Prepare message data
    const messageData: TeacherToolChatSendMessageRequestDto = {
      content: inputValue,
    };

    // Send the message to the API
    sendMessage({
      id: Number(chatId),
      data: messageData,
    });

    // Clear input directly again
    setInputValue("");
  }

  // Handle keyboard events in the input field
  function handleKeyDown(e: React.KeyboardEvent) {
    if (isWaitingForResponse) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }

  // Handle copying message content
  const handleCopyMessage = (content: string, messageId?: number) => {
    if (messageId) {
      navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
    }
  };

  // Format timestamp for messages
  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    //am pm for en 24 hour format for tr
    return format(date, "p", {
      locale: i18n.language === "tr" ? tr : enUS,
    });
  };

  // Filter REGEN_TOOL messages except when it's the last message in the chat
  const filteredMessages = useMemo(() => {
    if (!chat?.messages?.length) return [];

    return chat.messages.filter((message, index) => {
      if (chat.messages) {
        // If this message is not a REGEN_TOOL message, keep it
        if (message.responseType !== "REGEN_TOOL") return true;

        // If this is the last message in the chat and it's a REGEN_TOOL, keep it
        if (index === chat?.messages?.length - 1) return true;

        // Otherwise filter out REGEN_TOOL messages
        return false;
      }
    });
  }, [chat?.messages]);

  const isWaitingForResponse:
    | "AI_RESPONSE_WAITING"
    | "REGEN_TOOL_WAITING"
    | undefined = useMemo(() => {
    const messageLength = chat?.messages?.length;
    if (!messageLength || messageLength === 0 || messageLength === 1)
      return undefined;

    const lastMessage = chat?.messages?.[messageLength - 1];

    queryClient.invalidateQueries({
      queryKey: Array.isArray(toolQueryKey) ? toolQueryKey : [toolQueryKey],
    });
    queryClient.invalidateQueries({
      queryKey: Array.isArray(toolsQueryKey) ? toolsQueryKey : [toolsQueryKey],
    });
    // Show loading when USER is waiting for a response
    if (lastMessage?.role === "USER") return "AI_RESPONSE_WAITING";

    // Show loading when last message is REGEN_TOOL and tool is regenerating
    if (lastMessage?.responseType === "REGEN_TOOL") return "REGEN_TOOL_WAITING";

    return undefined;
  }, [chat?.messages, toolQueryKey, toolsQueryKey, queryClient]);

  return (
    <div className="flex fixed bottom-8 right-2 items-end z-[51]">
      <motion.div
        className="relative"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, ease: "easeOut" }}
      >
        <div className="flex flex-col items-end">
          {/* Animated Chat Container */}
          <AnimatePresence mode="wait">
            {isExpanded && (
              <motion.div
                key="chat-container"
                variants={chatContainerVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="mb-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden relative transition-colors duration-300 border-0 ring-1 ring-gray-200 dark:ring-gray-700 w-auto max-w-[480px] min-w-[320px]"
                style={{
                  willChange: "transform, opacity",
                  boxShadow:
                    "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                }}
              >
                {/* Chat Header */}
                <div className="sticky top-0 left-0 right-0 h-14 bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 dark:from-indigo-700 dark:via-violet-700 dark:to-purple-700 z-20 flex items-center justify-between px-4">
                  <div className="text-white font-medium flex items-center gap-2.5">
                    <div className="p-1.5 bg-white/15 rounded-full backdrop-blur-sm">
                      <Sparkles size={16} className="text-white" />
                    </div>
                    <span className="text-sm font-semibold tracking-wide">
                      AstroMadlen
                    </span>
                  </div>
                  <motion.button
                    className="w-8 h-8 flex items-center justify-center cursor-pointer bg-white/15 rounded-full hover:bg-white/25 transition-colors duration-200"
                    onClick={() => setIsExpanded(false)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <X size={16} className="text-white" />
                  </motion.button>
                </div>

                <div className="flex flex-col h-full max-h-[calc(75vh-120px)]">
                  {/* Chat Messages Area */}
                  <div
                    className="flex-1 overflow-y-auto px-4 py-6 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 bg-gray-50 dark:bg-gray-800"
                    style={{
                      maskImage:
                        "linear-gradient(to bottom, transparent, black 10px, black calc(100% - 10px), transparent)",
                      WebkitMaskImage:
                        "linear-gradient(to bottom, transparent, black 10px, black calc(100% - 10px), transparent)",
                      minHeight: "240px",
                      maxHeight: "450px",
                      overscrollBehavior: "contain",
                    }}
                  >
                    {/* Welcome message if no messages yet */}
                    {(!filteredMessages || filteredMessages.length === 0) && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="flex flex-col items-center justify-center h-full text-center px-4 py-6"
                      >
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 overflow-hidden">
                          <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center relative">
                            <img
                              src={AstroMadlen_Profile_Picture}
                              alt="AstroMadlen"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
                          {t("toolChat.welcomeTitle") ||
                            "Welcome to AstroMadlen"}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 max-w-[280px]">
                          {t("toolChat.welcomeMessage") ||
                            "Ask me anything about this tool, request changes, or explore new ideas."}
                        </p>
                      </motion.div>
                    )}

                    {/* Message bubbles */}
                    <AnimatePresence initial={false}>
                      {filteredMessages?.map((message, idx) => {
                        const isUser =
                          "role" in message ? message.role === "USER" : false;
                        const messageContent =
                          "content" in message ? message.content || "" : "";
                        const isRegenTool =
                          "responseType" in message
                            ? message.responseType === "REGEN_TOOL"
                            : false;

                        const messageTime = message.createdAt
                          ? formatMessageTime(message.createdAt)
                          : "";

                        return (
                          <motion.div
                            key={idx}
                            custom={idx}
                            variants={messageVariants}
                            initial={false}
                            animate="visible"
                            exit="exit"
                            className={`mb-6 flex ${isUser ? "justify-end" : "justify-start"}`}
                          >
                            <div
                              className={`flex ${isUser ? "flex-row-reverse" : "flex-row"} items-end gap-2 max-w-[85%] group`}
                            >
                              {/* Avatar */}
                              {isUser ? (
                                <div className="rounded-full flex-shrink-0 w-8 h-8 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600">
                                  <User size={14} className="text-white" />
                                </div>
                              ) : (
                                <div className="rounded-full flex-shrink-0 w-8 h-8 bg-gradient-to-br from-violet-400 to-purple-500 overflow-hidden flex items-center justify-center relative">
                                  <img
                                    src={AstroMadlen_Profile_Picture}
                                    alt="AstroMadlen"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              )}

                              {/* Message Content */}
                              <div className="flex flex-col">
                                <div className="relative">
                                  {isWaitingForResponse ===
                                    "REGEN_TOOL_WAITING" &&
                                    isRegenTool && (
                                      <>
                                        {/* Glow Ring */}
                                        <motion.div
                                          className="absolute -inset-[4px] rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-fuchsia-500 opacity-25 blur-sm pointer-events-none z-0"
                                          animate={{
                                            scale: [1, 1.05, 1],
                                            opacity: [0.2, 0.4, 0.2],
                                          }}
                                          transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                            repeatType: "mirror",
                                            ease: "easeInOut",
                                          }}
                                        />

                                        {/* Animated Border */}
                                        <motion.div
                                          className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-indigo-500 via-purple-500 to-fuchsia-500 opacity-60 pointer-events-none z-0"
                                          animate={{
                                            backgroundPosition: [
                                              "0% 50%",
                                              "100% 50%",
                                              "0% 50%",
                                            ],
                                          }}
                                          transition={{
                                            duration: 6,
                                            repeat: Infinity,
                                            repeatType: "loop",
                                            ease: "linear",
                                          }}
                                          style={{
                                            backgroundSize: "300% 300%",
                                          }}
                                        />
                                      </>
                                    )}

                                  {/* Copy Button (Only for AI messages) */}
                                  {!isUser && (
                                    <motion.button
                                      initial={{ opacity: 0, scale: 0.8 }}
                                      animate={{ opacity: 1, scale: 1 }}
                                      onClick={() =>
                                        handleCopyMessage(
                                          messageContent,
                                          message.id,
                                        )
                                      }
                                      className={`absolute ${isUser ? "-left-10" : "-right-10"} top-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1.5 bg-white dark:bg-gray-700 rounded-full shadow-sm border border-gray-200 dark:border-gray-600`}
                                      title={t("common.copy")}
                                    >
                                      {copiedMessageId === message.id ? (
                                        <CheckCircle2
                                          size={14}
                                          className="text-green-500"
                                        />
                                      ) : (
                                        <Copy
                                          size={14}
                                          className="text-gray-500 dark:text-gray-400"
                                        />
                                      )}
                                    </motion.button>
                                  )}

                                  {/* Message Bubble */}
                                  <div
                                    className={`px-4 py-3 rounded-2xl overflow-hidden relative z-10 shadow-sm ${
                                      isUser
                                        ? "bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-br-sm"
                                        : "bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-700 rounded-bl-sm"
                                    }`}
                                  >
                                    <Markdown
                                      className={`prose dark:prose-invert max-w-none ${
                                        isUser
                                          ? "text-white prose-headings:text-white prose-strong:text-white prose-em:text-white prose-a:text-white"
                                          : "text-gray-800 dark:text-gray-100 prose-a:text-indigo-500 dark:prose-a:text-indigo-400"
                                      } prose-p:my-1 prose-li:my-1 prose-ul:my-1 prose-ol:my-1 prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-pre:my-1.5 prose-pre:rounded-md prose-code:text-violet-600 dark:prose-code:text-violet-400 prose-code:text-sm`}
                                      remarkPlugins={[remarkGfm]}
                                    >
                                      {messageContent}
                                    </Markdown>
                                  </div>
                                </div>

                                {/* Message Timestamp */}
                                <div
                                  className={`flex ${isUser ? "justify-end" : "justify-start"} mt-1`}
                                >
                                  <span className="text-[10px] text-gray-500 dark:text-gray-400 px-2">
                                    {messageTime}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}

                      {/* Thinking indicator displayed separately */}
                      {isWaitingForResponse === "AI_RESPONSE_WAITING" && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="flex items-end mb-6 gap-2"
                        >
                          {/* Avatar */}
                          <div className="rounded-full flex-shrink-0 w-8 h-8 bg-gradient-to-br from-violet-400 to-purple-500 overflow-hidden flex items-center justify-center relative">
                            <img
                              src={AstroMadlen_Profile_Picture}
                              alt="AstroMadlen"
                              className="w-full h-full object-cover"
                            />
                          </div>

                          <motion.div
                            className="bg-white dark:bg-gray-700 px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm border border-gray-100 dark:border-gray-700 flex items-center"
                            variants={thinkingVariants}
                            animate="animate"
                          >
                            <div className="flex space-x-1.5">
                              {[...Array(3)].map((_, i) => (
                                <motion.div
                                  key={i}
                                  className="w-2 h-2 bg-indigo-500 dark:bg-indigo-400 rounded-full"
                                  animate={{
                                    y: ["0%", "-50%", "0%"],
                                  }}
                                  transition={{
                                    duration: 0.6,
                                    repeat: Infinity,
                                    delay: i * 0.2,
                                    ease: "easeInOut",
                                  }}
                                />
                              ))}
                            </div>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <div ref={chatContainerRef} />
                  </div>

                  {/* Input area with auto-expanding textarea */}
                  <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <div className="relative px-3">
                      <AutoResizeTextarea
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t("assistant.typePlaceholder")}
                        minRows={1}
                        maxRows={5}
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={!!isWaitingForResponse || !inputValue.trim()}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full transition-all ${
                          isWaitingForResponse || !inputValue.trim()
                            ? "bg-gray-300 dark:bg-gray-600 cursor-not-allowed"
                            : "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm hover:shadow-md active:scale-95"
                        }`}
                      >
                        <Send
                          size={16}
                          className={
                            isWaitingForResponse || !inputValue.trim()
                              ? "text-gray-500 dark:text-gray-400"
                              : "text-white"
                          }
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Chat Bubble - Made entire bubble clickable */}
          {!isExpanded && (
            <motion.div
              className="absolute bottom-full right-0 mb-8 cursor-pointer"
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              onClick={handleMascotClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {/* Gradient background with animation */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r rounded-xl from-indigo-400/20 via-violet-400/20 to-purple-400/20"
                animate={{
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "linear",
                }}
                style={{
                  backgroundSize: "200% 200%",
                }}
              />

              {/* Main bubble content */}
              <div className="relative px-4 py-2.5 rounded-xl bg-white dark:bg-gray-800 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 shadow-lg">
                {/* Sparkles */}
                <AnimatePresence>
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute"
                      style={{
                        top: `${20 + i * 30}%`,
                        left: `${10 + i * 40}%`,
                      }}
                      variants={sparkleVariants}
                      initial="initial"
                      animate="animate"
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: i * 0.4,
                      }}
                    >
                      <Sparkles
                        size={12}
                        className="text-indigo-400 dark:text-indigo-300"
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Text content */}
                <div className="relative flex items-center gap-2">
                  <div className="p-1 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full">
                    <Sparkles size={12} className="text-white" />
                  </div>
                  <Text
                    variant="p"
                    className="whitespace-nowrap text-sm font-medium bg-gradient-to-r from-indigo-600 via-purple-500 to-indigo-500 dark:from-indigo-400 dark:via-purple-300 dark:to-indigo-300 text-transparent bg-clip-text"
                  >
                    {isCreatingChat
                      ? t("common.loading")
                      : t("toolChat.startConversation")}
                  </Text>
                </div>
              </div>
              {/* Bubble Tail */}
              <div className="-z-10 absolute -bottom-2 right-8 w-4 h-4 bg-white dark:bg-gray-800 border-b border-r border-gray-200/50 dark:border-gray-700/50 transform rotate-45 backdrop-blur-sm"></div>
            </motion.div>
          )}

          {/* Mascot - always rendered */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            animate={floatingAnimation}
            onClick={handleMascotClick}
            whileTap={{ scale: 0.9 }}
            transition={{
              type: "spring",
              stiffness: 300,
              scale: { duration: 0.2 },
            }}
            className="cursor-pointer relative"
          >
            {/* Glow effect behind mascot */}
            <motion.div
              className="absolute inset-0 bg-indigo-300 dark:bg-indigo-600 rounded-full blur-xl opacity-30"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.2, 0.4, 0.2],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Status indicator - Active/typing when chat is open */}
            {isExpanded && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="absolute top-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800 z-20"
              />
            )}

            <motion.img
              src={astroMadlen}
              alt="Madlen Mascot"
              className="w-20 relative z-10"
              animate={{ scale: isExpanded ? 0.9 : 1 }}
              transition={{ duration: 0.3 }}
              style={{ filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.15))" }}
            />
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
